import {purgeHydrationCache} from "@khanacademy/wonder-blocks-data";
import {RequestInfo} from "@khan/shared-core";

import checkContentExistsInEnglish from "./check-content-exists-in-english.ts";
import fetchContentData from "./fetch-content-data.ts";
import type {ContentData, ContentOptions} from "./types.ts";

const ContentCacheScope = "content-";

export const isContentRequestId = (requestId: string): boolean =>
    requestId.startsWith(ContentCacheScope);

export const clearContentDataCache = () => {
    purgeHydrationCache((key, cacheEntry) => isContentRequestId(key));
};

export const contentDataRequestHandler = async <TData extends ContentData>(
    options: ContentOptions,
): Promise<TData> => {
    const data = await fetchContentData(options);
    if (data.isNotFound && RequestInfo.kaLocale !== "en") {
        return {
            ...data,
            existsInEnglish: await checkContentExistsInEnglish(options.path),
        };
    }

    if (!data.componentProps) {
        return data;
    }

    const {componentProps} = data;

    return (componentProps && componentProps.curation) || componentProps;
};

export const getContentDataRequestId = (options: ContentOptions): string => {
    try {
        const optionsAsString = JSON.stringify(options);
        /**
         * Encode the locale in the id; this may be useful if we start
         * caching data in local storage and want to look that up somehow.
         *
         * We use WB Data hydration because content data is fetched via
         * `standaloneGqlFetch`, which is promise-based and as such needs
         * to be tracked for fulfillment. In addition, since this is outside
         * of our React components, we need to use WB Data so that the result
         * is included in the hydration cache.
         */
        return `${ContentCacheScope}${RequestInfo.kaLocale}-${optionsAsString}`;
    } catch (e: any) {
        throw new Error(`Failed to auto-generate key: ${e}`);
    }
};
